import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatFooterCellDef,
  MatFooterCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatFooterRowDef,
  MatFooterRow,
} from '@angular/material/table';
import { Router, ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Subscription, throwError } from 'rxjs';

import { NewBrandComponent } from '@app/brand/edit-brand/edit-business-info/new-brand/new-brand.component';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { Tenant } from '@generated/tcea/models';
import { BrandService } from '@services/brand/1.0/brand.service';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { UtilitiesService } from '@services/utilities/1.0/utilities.service';
import { ResellerService } from '@services/reseller/reseller.service';
import { ResellerMasterdata } from '@generated/tcea/models/reseller-masterdata';
import { DecoratorService } from '@services/decorator/1.0/decorator.service';
import { map, take } from 'rxjs/operators';
import { NgIf, NgClass, NgFor, AsyncPipe } from '@angular/common';
import { LoadingComponent } from '@components/loading/1.0/loading.component';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ButtonComponent } from '@components/button/1.0/button.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'txp-brand-detail',
  templateUrl: './brand-detail.component.html',
  styleUrls: ['./brand-detail.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    LoadingComponent,
    MatFormField,
    MatPrefix,
    MatInput,
    MatSelect,
    NgClass,
    FormsModule,
    NgFor,
    MatOption,
    MatIconButton,
    MatSuffix,
    MatIcon,
    ButtonComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatFooterCellDef,
    MatFooterCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFooterRowDef,
    MatFooterRow,
    TranslateModule,
    AsyncPipe,
  ],
})
export class BrandDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() closeParent: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild(MatSort) sort: MatSort;
  loadingColor = LoadingColor; // expose enum to template
  loadingType = LoadingType; // expose enum to template
  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize; // expose enum to template
  loading = false;
  brandDetail: Tenant[] = [];
  canCreateBrand$ = new BehaviorSubject(false);
  dataSource: MatTableDataSource<Tenant> = new MatTableDataSource<Tenant>();
  displayColumns: string[] = ['name', 'countryCode', 'alias'];
  isLoading = true;
  isStillLoading = false;
  searchCriteria = '';
  subscription: Subscription;
  resellerFilterSubscription: Subscription;
  // Reseller filter properties
  resellers: ResellerMasterdata[] = [];
  filteredResellers: ResellerMasterdata[] = [];
  selectedReseller: ResellerMasterdata | null = null;
  resellerInputFocused = false;
  showResellerFilter = false;
  resellerMap: Map<string, string> = new Map();
  resellerLogoMap: Map<string, string> = new Map(); // Cache for reseller logos
  private resellersLoaded = false;
  private logosLoaded = false;

  constructor(
    private brandService: BrandService,
    private dialog: MatDialog,
    private permissionsService: PermissionsService,
    private resellerService: ResellerService,
    private router: Router,
    private route: ActivatedRoute,
    private utilities: UtilitiesService,
    private decoratorService: DecoratorService,
  ) {
    this.brandService.currentBrandBehaviorSubject.subscribe(() => {
      this.applyFilters();
    });
  }

  ngOnInit() {
    // Subscribe to the observable from BrandService
    this.resellerFilterSubscription = this.brandService.showResellerFilter$.subscribe(
      (shouldShow) => {
        this.showResellerFilter = shouldShow;

        // Update displayColumns if needed
        if (this.showResellerFilter && !this.displayColumns.includes('reseller')) {
          this.displayColumns = ['name', 'countryCode', 'alias', 'reseller'];
        }

        // Load resellers if needed
        if (this.showResellerFilter && this.resellers.length === 0) {
          this.loadResellers();
        }

        // Fetch all reseller logos when filter is shown
        if (this.showResellerFilter) {
          this.fetchAllResellerLogos();
        }
      },
    );

    this.getBrands();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy() {
    // unsubscribe so it doesn't keep fetching brands
    if (this.subscription?.unsubscribe) {
      this.subscription.unsubscribe();
    }
    if (this.resellerFilterSubscription?.unsubscribe) {
      this.resellerFilterSubscription.unsubscribe();
    }
  }

  /**
   * Get all brands and populate the data source as the data
   * is observed
   */
  getBrands(): void {
    this.brandDetail = [];

    this.subscription = this.brandService.get().subscribe({
      next: (brands: Tenant[]) => {
        const isResellerProductOperations = this.permissionsService.getRole(
          'ResellerProductOperations',
        );
        // Update after the component has been initialized so the observable sees the change.
        this.canCreateBrand$.next(
          this.permissionsService.getRole('SuperAdmin') || isResellerProductOperations,
        );
        this.brandDetail = this.brandDetail.concat(brands);

        // refresh the data using applyFilters to handle both search and reseller filters
        this.applyFilters();

        this.isLoading = false;
        this.isStillLoading = true;
      },
      error: (err) => {
        throwError(err);
        this.isLoading = false;
        this.isStillLoading = false;
      },
      complete: () => {
        this.isStillLoading = false;
        this.isLoading = false;
      },
    });
  }

  filter(value) {
    this.searchCriteria = value;
    this.applyFilters();
  }

  createBrand(event) {
    const dialogRef = this.dialog.open(NewBrandComponent, {
      maxHeight: '95vh',
      panelClass: 'new-brand-dialog',
    });
    this.closeParent.emit();

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.getBrands();
      }
    });
  }

  async setBrand(brand: Tenant): Promise<void> {
    if (brand.id === this.brandService.currentBrandBehaviorSubject?.value?.id) return;

    this.closeParent.emit();

    // If brand has a resellerId, set the logo from cache before page reload
    if (brand.resellerId) {
      this.setResellerLogo(brand.resellerId);
    }

    const url = this.router.url;
    const firstGUIDIdx = this.utilities.indexOfUrlGUID(url);
    const nextRoute = firstGUIDIdx === -1 ? url : url.substring(0, firstGUIDIdx - 1);
    localStorage.setItem('active_tenant_identifier', brand.id);
    this.brandService.reloadWithNewBrand(brand.id, nextRoute);
  }

  resetFilters() {
    this.dataSource.filter = '';
    this.searchCriteria = '';
    this.selectedReseller = null;
    this.applyFilters();
  }

  /**
   * Load all resellers
   */
  loadResellers(): void {
    this.resellerService.getResellers().subscribe({
      next: (resellers: ResellerMasterdata[]) => {
        this.resellers = resellers;
        this.filteredResellers = [...resellers];
        // Create map for quick lookup
        this.resellerMap.clear();
        resellers.forEach((reseller) => {
          this.resellerMap.set(reseller.id, reseller.name);
        });
        this.resellersLoaded = true;
        this.checkAndPreSelectReseller();
      },
      error: (err) => {
        throwError(err);
      },
    });
  }

  /**
   * Check for resellerId query parameter and pre-select reseller
   */
  private checkAndPreSelectReseller(): void {
    // Only proceed if both resellers and logos are loaded
    if (!this.resellersLoaded || !this.logosLoaded) {
      return;
    }

    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      const resellerId = params['resellerId'];
      if (resellerId && this.showResellerFilter && this.resellers.length > 0) {
        const foundReseller = this.resellers.find((r) => r.id === resellerId);
        if (foundReseller) {
          this.selectedReseller = foundReseller;
          this.applyFilters();
          this.setResellerLogo(resellerId);
          // Clear query parameter from URL
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true,
          });
        }
      }
    });
  }

  /**
   * Filter items based on search text
   */
  filterItems(searchText: string, list: any[], key: string): any[] {
    if (!searchText) {
      return list;
    }
    return list.filter((item) => item[key]?.toLowerCase()?.includes(searchText.toLowerCase()));
  }

  /**
   * Handle reseller selection and filter brands
   */
  onResellerSelected(reseller: ResellerMasterdata | null): void {
    this.selectedReseller = reseller;
    this.applyFilters();

    // Only set logo if we're on brand list view (no brand selected)
    if (!this.brandService.currentBrand?.isSet) {
      if (reseller) {
        this.setResellerLogo(reseller.id);
      } else {
        // Clear logo if reseller is null
        this.brandService.setResellerLogoUrl(null);
      }
    }
  }

  /**
   * Reset filtered resellers list
   */
  resetFilteredResellers(): void {
    this.filteredResellers = [...this.resellers];
  }

  /**
   * Clear selected reseller and reset filter
   */
  clearResellerSelection(event: Event): void {
    event.stopPropagation();
    const resellerIdToClear = this.selectedReseller?.id;
    this.selectedReseller = null;
    this.resetFilteredResellers();
    this.applyFilters();

    // Only clear logo if we're on brand list view (no brand selected)
    if (!this.brandService.currentBrand?.isSet && resellerIdToClear) {
      this.brandService.setResellerLogoUrl(null, resellerIdToClear);
    }
  }

  /**
   * Get reseller name from resellerId
   */
  getResellerName(resellerId: string | undefined): string {
    if (!resellerId) {
      return '';
    }
    return this.resellerMap.get(resellerId) || '';
  }

  /**
   * Fetch all reseller logos from GLOBAL decorators and cache them
   */
  private fetchAllResellerLogos(): void {
    this.decoratorService
      .get('GLOBAL', 'cross-tenant:reseller-logo')
      .pipe(
        map((decorators) => {
          // Clear and rebuild the logo map
          this.resellerLogoMap.clear();
          decorators.forEach((d) => {
            if (d.field === 'images' && d.key && d.value) {
              this.resellerLogoMap.set(d.key, d.value);
            }
          });
        }),
      )
      .subscribe(() => {
        this.logosLoaded = true;
        // After logos are fetched, check for query parameter
        this.checkAndPreSelectReseller();
      });
  }

  /**
   * Set reseller logo from cached map or sessionStorage
   */
  private setResellerLogo(resellerId: string): void {
    // First check sessionStorage (for ResellerProductOperations users, logo is stored there)
    const cachedLogoUrl = sessionStorage.getItem(`reseller_logo_url_${resellerId}`);
    if (cachedLogoUrl) {
      this.brandService.setResellerLogoUrl(cachedLogoUrl, resellerId);
      return;
    }

    // Fallback to resellerLogoMap (for other users, logos are fetched from GLOBAL)
    const logoUrl = this.resellerLogoMap.get(resellerId) || null;
    this.brandService.setResellerLogoUrl(logoUrl, resellerId);
  }

  /**
   * Apply both search and reseller filters
   */
  private applyFilters(): void {
    let filteredBrands = this.brandDetail.filter(
      ({ id }) => id !== this.brandService.currentBrand?.id,
    );

    // Apply reseller filter if selected
    if (this.selectedReseller) {
      filteredBrands = filteredBrands.filter(
        (brand) => brand.resellerId === this.selectedReseller?.id,
      );
    }

    // Apply search filter
    const searchText = this.searchCriteria.trim().toLowerCase();
    if (searchText) {
      filteredBrands = filteredBrands.filter(
        (brand) =>
          brand.id?.toLowerCase().includes(searchText) ||
          brand.name?.toLowerCase().includes(searchText) ||
          brand.countryCode?.toLowerCase().includes(searchText) ||
          brand.alias?.toLowerCase().includes(searchText),
      );
    }

    this.dataSource.data = filteredBrands;
  }
}
